import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, CreditCard, Package, TrendingUp, Users, Shield, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const EcommerceDevelopment = () => {
  const navigate = useNavigate();

  const ecommerceServices = [
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "Custom E-commerce Stores",
      description: "Fully customized online stores built from scratch to match your brand and business requirements.",
      features: ["Custom Design", "Product Catalog", "Shopping Cart", "Order Management"],
      price: "₹1,50,000+",
      timeline: "8-12 weeks"
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Multi-vendor Marketplace",
      description: "Comprehensive marketplace platforms where multiple vendors can sell their products.",
      features: ["Vendor Dashboard", "Commission System", "Multi-payment Gateway", "Review System"],
      price: "₹3,00,000+",
      timeline: "12-16 weeks"
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Payment Integration",
      description: "Secure payment gateway integration supporting multiple payment methods and currencies.",
      features: ["Multiple Gateways", "Wallet Integration", "EMI Options", "Subscription Billing"],
      price: "₹50,000+",
      timeline: "2-4 weeks"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "E-commerce Analytics",
      description: "Advanced analytics and reporting tools to track sales, customer behavior, and business metrics.",
      features: ["Sales Analytics", "Customer Insights", "Inventory Reports", "ROI Tracking"],
      price: "₹75,000+",
      timeline: "4-6 weeks"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Customer Management",
      description: "Comprehensive customer management system with loyalty programs and personalization.",
      features: ["Customer Profiles", "Loyalty Programs", "Wishlist", "Order History"],
      price: "₹1,00,000+",
      timeline: "6-8 weeks"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security & Compliance",
      description: "Enterprise-grade security features ensuring data protection and compliance standards.",
      features: ["SSL Certificates", "PCI Compliance", "Data Encryption", "Fraud Detection"],
      price: "₹1,25,000+",
      timeline: "4-6 weeks"
    }
  ];

  const platforms = [
    {
      name: "Custom React/Node.js",
      description: "Built from scratch for maximum customization",
      bestFor: "Large enterprises with specific requirements",
      features: ["Complete Customization", "Scalable Architecture", "Advanced Features", "API Integration"]
    },
    {
      name: "Shopify Plus",
      description: "Enterprise Shopify solutions",
      bestFor: "Medium to large businesses",
      features: ["Quick Setup", "App Ecosystem", "Mobile Optimized", "Built-in SEO"]
    },
    {
      name: "WooCommerce",
      description: "WordPress-based e-commerce solution",
      bestFor: "Small to medium businesses",
      features: ["Cost Effective", "Plugin Support", "SEO Friendly", "Content Management"]
    },
    {
      name: "Magento Commerce",
      description: "Enterprise e-commerce platform",
      bestFor: "Large businesses with complex needs",
      features: ["Multi-store Management", "B2B Features", "Advanced Marketing", "Scalability"]
    }
  ];

  const features = [
    {
      title: "Mobile-First Design",
      description: "Responsive designs optimized for mobile commerce",
      percentage: "80%"
    },
    {
      title: "Fast Loading Speed",
      description: "Optimized performance for better user experience",
      percentage: "3s"
    },
    {
      title: "Conversion Optimization",
      description: "Features designed to maximize sales conversion",
      percentage: "25%"
    },
    {
      title: "SEO Optimized",
      description: "Built-in SEO features for better search rankings",
      percentage: "100%"
    }
  ];

  const testimonials = [
    {
      name: "Amit Singh",
      company: "Fashion Hub",
      role: "Founder",
      text: "Our online sales increased by 300% after launching the new e-commerce platform. The team delivered exactly what we needed.",
      rating: 5
    },
    {
      name: "Neha Gupta",
      company: "Electronics World",
      role: "CEO",
      text: "The multi-vendor marketplace they built for us handles 1000+ vendors seamlessly. Outstanding technical expertise!",
      rating: 5
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>E-commerce Development Services - Online Store Solutions | Vibhraad Technology</title>
        <meta name="description" content="Professional e-commerce development services. Custom online stores, multi-vendor marketplaces, payment integration, and mobile commerce solutions. Boost your online sales." />
        <meta name="keywords" content="ecommerce development, online store development, shopping cart development, multi vendor marketplace, payment gateway integration, mobile commerce" />
      </Helmet>

      {/* Hero Section */}
      <motion.section
        className="relative h-[70vh] bg-gray-800 overflow-hidden flex items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Animated Background */}
        <motion.div className="absolute inset-0 opacity-20">
          {Array.from({ length: 12 }).map((_, i) => (
            <motion.div
              key={`cart-${i}`}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 360]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 0.5
              }}
            >
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 rounded-lg transform rotate-3" />
                <div className="relative bg-white px-6 py-2 rounded-lg border-2 border-blue-600">
                  <span className="text-blue-600 font-bold">E-commerce Solutions</span>
                </div>
              </div>
            </motion.div>

            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Boost Your <br />
              <span className="text-blue-600">Online Sales</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Custom e-commerce development services to create powerful online stores, marketplaces, 
              and digital commerce solutions that drive sales and growth.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button
                onClick={() => navigate('/contact')}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                Start Your Store <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/projects')}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-800 transition-colors"
              >
                View E-commerce Projects
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl font-bold mb-4">Why Choose Our E-commerce Solutions?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our e-commerce platforms are designed for performance, scalability, and conversion optimization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-5xl font-bold text-blue-600 mb-4">{feature.percentage}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl font-bold mb-4">E-commerce Development Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive e-commerce solutions to help you sell more online and grow your business.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ecommerceServices.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-blue-600 mb-6">{service.icon}</div>
                <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-semibold text-blue-600">{service.price}</span>
                    <span className="text-sm text-gray-500">{service.timeline}</span>
                  </div>
                  <button
                    onClick={() => navigate('/contact')}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Get Quote
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl font-bold mb-4">E-commerce Platforms We Work With</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We choose the right platform based on your business needs, budget, and growth plans.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-xl font-bold mb-2">{platform.name}</h3>
                <p className="text-gray-600 mb-4">{platform.description}</p>
                <div className="text-sm text-blue-600 font-medium mb-4">Best for: {platform.bestFor}</div>
                <div className="grid grid-cols-2 gap-2">
                  {platform.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl font-bold mb-4">Client Success Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how our e-commerce solutions have helped businesses increase their online sales.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6">"{testimonial.text}"</p>
                <div>
                  <h4 className="font-bold">{testimonial.name}</h4>
                  <p className="text-gray-500">{testimonial.role}, {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Launch Your Online Store?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's create a powerful e-commerce platform that drives sales and grows your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Get E-commerce Quote <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                View Pricing Plans
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EcommerceDevelopment;